import React, { createContext, useCallback, useContext, FunctionComponent, ReactNode, useMemo, useState } from 'react';

import { PeopleFilterType, PeopleSortType } from '@components/people_tags/customPeopleTagTypes';

export interface PeopleManagementState {
  search: string;
  filter: PeopleFilterType;
  sort: string;
  page: number;
  showHiddenPeople: boolean;
}

interface PeopleManagementContextType {
  state: PeopleManagementState;
  updateSearch: (search: string) => void;
  updateFilter: (filter: PeopleFilterType) => void;
  updateSort: (sort: string) => void;
  updatePage: (page: number) => void;
  updateShowHiddenPeople: (showHidden: boolean) => void;
}

const PeopleManagementContext = createContext<PeopleManagementContextType | undefined>(undefined);

interface PeopleManagementProviderProps {
  children: ReactNode;
}

export const PeopleManagementProvider: FunctionComponent<PeopleManagementProviderProps> = ({
  children,
}) => {
  const [state, setState] = useState<PeopleManagementState>({
    search: '',
    filter: PeopleFilterType.All,
    sort: PeopleSortType.NamedFirst,
    page: 1,
    showHiddenPeople: true,
  });

  const updateSearch = useCallback((search: string): void => {
    setState((prev) => ({ ...prev, search, page: 1 }));
  }, []);

  const updateFilter = useCallback((filter: PeopleFilterType): void => {
    setState((prev) => ({ ...prev, filter, page: 1 }));
  }, []);

  const updateSort = useCallback((sort: string): void => {
    setState((prev) => ({ ...prev, sort, page: 1 }));
  }, []);

  const updatePage = useCallback((page: number): void => {
    setState((prev) => ({ ...prev, page }));
  }, []);

  const updateShowHiddenPeople = useCallback((showHiddenPeople: boolean): void => {
    setState((prev) => ({ ...prev, showHiddenPeople, page: 1 }));
  }, []);

  const contextValue = useMemo<PeopleManagementContextType>(() => ({
    state,
    updateSearch,
    updateFilter,
    updateSort,
    updatePage,
    updateShowHiddenPeople,
  }), [state, updateSearch, updateFilter, updateSort, updatePage, updateShowHiddenPeople]);

  return (
    <PeopleManagementContext.Provider value={contextValue}>
      {children}
    </PeopleManagementContext.Provider>
  );
};

export const usePeopleManagementContext = (): PeopleManagementContextType => {
  const context = useContext(PeopleManagementContext);
  if (!context) {
    throw new Error('usePeopleManagementContext must be used within a PeopleManagementProvider');
  }
  return context;
};
